import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PssActionType, PssCharacterAction, PssCharacterDesign, PssConditionType, PssCrewMember, PssInventoryItem, PssItemDesign } from '../../../services/pss-auth.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { AiEditorComponent, AiImportPayload, CompareTarget } from '../ai-editor/ai-editor.component';
import { spriteIdOrEmpty, equipSlotLabel } from '../pss-display-utils';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-crew-detail-modal',
  standalone: true,
  imports: [CommonModule, AiEditorComponent, PssSpriteComponent],
  templateUrl: './crew-detail-modal.component.html',
  styleUrls: ['../dashboard.component.css']
})
export class CrewDetailModalComponent {
  get canEditAi(): boolean { return this.featureFlags.isBasicPlus(); }

  @ViewChild('crewModal') crewModalRef!: ElementRef;

  constructor(private featureFlags: FeatureFlagService) {}

  @Input() crew: PssCrewMember | null = null;
  @Input() characterDesigns = new Map<string, PssCharacterDesign>();
  @Input() itemDesigns = new Map<string, PssItemDesign>();
  @Input() rules: PssCharacterAction[] = [];
  @Input() conditionTypes: PssConditionType[] = [];
  @Input() actionTypes: PssActionType[] = [];
  @Input() inventory: PssInventoryItem[] = [];
  @Input() showAi = false;
  @Input() aiSaving = false;
  @Input() aiError = '';
  @Input() compareAiSaving = false;
  @Input() compareAiError = '';
  @Input() initialConditionId = '1';
  @Input() initialActionId = '1';
  @Input() compareTargets: CompareTarget[] = [];
  @Input() userId = '';
  @Input() allianceId = '';
  @Input() userName = '';

  @Output() close = new EventEmitter<void>();
  @Output() toggleAi = new EventEmitter<void>();
  @Output() addRule = new EventEmitter<{ conditionTypeId: string; actionTypeId: string }>();
  @Output() deleteRule = new EventEmitter<PssCharacterAction>();
  @Output() moveRule = new EventEmitter<{ rule: PssCharacterAction; newIndex: number }>();
  @Output() importRules = new EventEmitter<AiImportPayload>();
  @Output() deleteAllRules = new EventEmitter<void>();
  @Output() deleteAllCompareRules = new EventEmitter<string>();
  @Output() editRule = new EventEmitter<{ rule: any; conditionTypeId: string; actionTypeId: string }>();
  @Output() compareEditRule = new EventEmitter<{ targetId: string; rule: any; conditionTypeId: string; actionTypeId: string }>();

  @Output() compareAddRule = new EventEmitter<{ targetId: string; conditionTypeId: string; actionTypeId: string }>();
  @Output() compareDeleteRule = new EventEmitter<{ targetId: string; rule: any }>();
  @Output() compareMoveRule = new EventEmitter<{ targetId: string; rule: any; newIndex: number }>();
  @Output() compareImportRules = new EventEmitter<{ targetId: string; payload: AiImportPayload }>();
  @Output() copyFromCompare = new EventEmitter<string>();

  onToggleAi(): void {
    const opening = !this.showAi;
    this.toggleAi.emit();
    if (opening) {
      setTimeout(() => {
        const el = this.crewModalRef?.nativeElement as HTMLElement | undefined;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  }

  crewName(c: PssCrewMember): string {
    return c.customName || this.characterDesigns.get(c.designId)?.name || `#${c.designId}`;
  }

  private static readonly CREW_STAT_KEY_TO_ITEM_TYPE: Partial<Record<keyof PssCrewMember, string>> = {
    weaponImp: 'Weapon',
    attackImp: 'Attack',
    scienceImp: 'Science',
    engineImp: 'Engine',
    pilotImp: 'Pilot',
    repairImp: 'Repair',
    abilityImp: 'Ability',
    hpImp: 'HP',
  };

  private static readonly CREW_STAT_KEY_TO_CHARACTER_FIELD: Partial<Record<keyof PssCrewMember, keyof PssCharacterDesign>> = {
    weaponImp: 'finalWeapon',
    attackImp: 'finalAttack',
    scienceImp: 'finalScience',
    engineImp: 'finalEngine',
    pilotImp: 'finalPilot',
    repairImp: 'finalRepair',
    abilityImp: 'specialAbilityArg',
    hpImp: 'finalHp',
  };

  crewRarity(designId: string): string {
    return this.characterDesigns.get(designId)?.rarity ?? '';
  }

  private getCrewGearBoost(c: PssCrewMember, key: keyof PssCrewMember): number {
    const requiredType = CrewDetailModalComponent.CREW_STAT_KEY_TO_ITEM_TYPE[key];
    if (!requiredType) return 0;
    const requiredLower = requiredType.toLowerCase();

    const equipment = this.crewEquipment(c);
    let total = 0;
    for (const eq of equipment) {
      const statEntries = this.itemStatEntriesForInstance(eq.designId, eq.instanceId || undefined);
      for (const entry of statEntries) {
        if (entry.stat.trim().toLowerCase() === requiredLower) {
          total += entry.value;
        }
      }
    }
    return total;
  }

  private crewBaseStat(c: PssCrewMember, key: keyof PssCrewMember): number {
    const charDesign = this.characterDesigns.get(c.designId);
    const charField = CrewDetailModalComponent.CREW_STAT_KEY_TO_CHARACTER_FIELD[key];
    if (!charDesign || !charField) return 0;
    return parseFloat((charDesign[charField] as string) || '0') || 0;
  }

  private crewTrainingPct(c: PssCrewMember, key: keyof PssCrewMember): number {
    return parseInt((c[key] as string) || '0', 10) || 0;
  }

  crewTotalStat(c: PssCrewMember, key: keyof PssCrewMember): number {
    if (key === 'abilityImp') {
      const total = this.abilityGearFallback(c);
      return Number.isFinite(total) ? total : 0;
    }

    const base = this.crewBaseStat(c, key);
    const training = this.crewTrainingPct(c, key);
    const gear = this.getCrewGearBoost(c, key);

    const baseWithTraining = base * (1 + training / 100); // base * (1 + training%)
    const statTotal = baseWithTraining + gear;
    return Number.isFinite(statTotal) ? statTotal : 0;
  }

  hasAbilityStat(c: PssCrewMember): boolean {
    return this.crewTotalStat(c, 'abilityImp') > 0 || !!this.characterDesigns.get(c.designId)?.specialAbilityType;
  }

  abilityBreakdown(c: PssCrewMember): string {
    const base = this.crewBaseStat(c, 'abilityImp');
    const training = this.crewTrainingPct(c, 'abilityImp');
    const gear = this.getCrewGearBoost(c, 'abilityImp');
    const baseWithTraining = base * (1 + training / 100);
    const total = baseWithTraining * (1 + gear / 100);
    return `base=${base} training=${training}% gear=${gear.toFixed(2)}% pre=${baseWithTraining.toFixed(2)} total=${total.toFixed(2)}`;
  }

  private abilityGearFallback(c: PssCrewMember): number {
    const base = this.crewBaseStat(c, 'abilityImp');
    const training = this.crewTrainingPct(c, 'abilityImp');
    const gear = this.getCrewGearBoost(c, 'abilityImp');
    const baseWithTraining = base * (1 + training / 100);
    if (baseWithTraining > 0) {
      return baseWithTraining * (1 + gear / 100);
    }
    // If no base/training ability, fall back to gear percentage as nominal ability for visibility.
    return gear;
  }

  private parseBonusValue(raw?: string): number {
    if (!raw) return 0;
    const match = raw.trim().match(/[-+]?\d+(?:\.\d+)?/);
    if (!match) return 0;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private collectItemStatEntries(entries: Array<{stat:string; value:number}>, type: string | undefined, value: string | undefined): void {
    if (!type) return;
    const stat = type.trim();
    if (!stat || stat.toLowerCase() === 'none') return;
    const parsed = this.parseBonusValue(value);
    if (parsed === 0) return;
    entries.push({ stat, value: parsed });
  }

  private itemDesignStatEntries(design: PssItemDesign): Array<{stat:string; value:number}> {
    const entries: Array<{stat:string; value:number}> = [];
    this.collectItemStatEntries(entries, design.enhancementType, design.enhancementValue || design.modifier);
    this.collectItemStatEntries(entries, design.enhancementType2, design.enhancementValue2 || design.modifier2);
    this.collectItemStatEntries(entries, design.bonusEnhancementType, design.bonusEnhancementValue);
    this.collectItemStatEntries(entries, design.bonusEnhancementType2, design.bonusEnhancementValue2);
    return entries;
  }

  private itemInstanceBonusEntries(instanceId: string): Array<{stat:string; value:number}> {
    const entries: Array<{stat:string; value:number}> = [];
    const inv = this.inventory.find(item => item.equippedItemIds?.includes(instanceId));
    if (!inv || !inv.instanceBonuses) return entries;
    for (const inst of inv.instanceBonuses.filter(b => b.itemId === instanceId)) {
      this.collectItemStatEntries(entries, inst.enhancementType, inst.enhancementValue);
      this.collectItemStatEntries(entries, inst.enhancementType2, inst.enhancementValue2);
    }
    return entries;
  }

  itemStatEntriesForInstance(designId: string, instanceId?: string): Array<{stat:string; value:number}> {
    const design = this.itemDesigns.get(designId);
    if (!design) return [];
    const entries = this.itemDesignStatEntries(design);
    if (instanceId) {
      const instanceEntries = this.itemInstanceBonusEntries(instanceId);
      entries.push(...instanceEntries);
    }
    return entries;
  }

  crewEquipment(c: PssCrewMember): Array<{ instanceId: string; designId: string }> {
    const equipment: Array<{ instanceId: string; designId: string }> = [];

    // Prioritize actual equipped instances for exact per-item bonuses.
    if (c.equipItemIds && c.equipItemIds.length > 0) {
      for (const instanceId of c.equipItemIds) {
        const inv = this.inventory.find(item => item.equippedItemIds?.includes(instanceId));
        if (inv) {
          equipment.push({ instanceId, designId: inv.designId });
          continue;
        }
        // Fallback: if no instance mapping is available, try using designId by instance->design lookup.
        const candidate = this.inventory.find(item => item.equippedBy.some(crew => crew.characterId === c.characterId));
        if (candidate) {
          equipment.push({ instanceId, designId: candidate.designId });
          continue;
        }
      }
    }

    // If we still have no equipment list, fall back to Crew design IDs.
    if (!equipment.length && c.equipDesignIds && c.equipDesignIds.length > 0) {
      for (const designId of c.equipDesignIds) {
        equipment.push({ instanceId: '', designId });
      }
    }

    return equipment;
  }

  crewSpriteId(designId: string): string {
    return spriteIdOrEmpty(this.characterDesigns.get(designId)?.profileSpriteId);
  }

  hasTraining(c: PssCrewMember): boolean {
    return ['hpImp', 'pilotImp', 'attackImp', 'weaponImp', 'scienceImp', 'engineImp', 'abilityImp', 'repairImp']
      .some(k => (c as any)[k] !== '0');
  }

  equipSlotLabel(subtype: string): string {
    return equipSlotLabel(subtype);
  }

  itemName(designId: string): string {
    return this.itemDesigns.get(designId)?.name ?? `#${designId}`;
  }
}
